import { Stack, MultiSelect, Drawer, ActionIcon, Tooltip } from '@mantine/core'
import { useMemo, useState } from 'react'
import { TbSettings } from 'react-icons/tb'
import { TableColumn } from './Table/GenericTable'
import { useTranslation } from 'react-i18next'

interface TableConfigProps<T> {
  columns: TableColumn<T>[]
  onColumnsChange: (columns: TableColumn<T>[]) => void
  title?: string
}

export function ConfigureView<T>({ columns, onColumnsChange, title }: TableConfigProps<T>) {
  const { t } = useTranslation('common')
  const resolvedTitle = title ?? t('configureTableView')
  const [configOpened, setConfigOpened] = useState(false)

  const handleColumnToggle = (event: string[], all: boolean) => {
    const updatedColumns = columns.map(col => (all ? { ...col, visible: event.includes(String(col.key)) } : col))
    onColumnsChange(updatedColumns)
  }

  const data = useMemo(() => {
    return columns
      .map(column => ({
        label: column.label,
        value: String(column.key),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [columns])

  return (
    <>
      <Tooltip label={resolvedTitle} arrowOffset={10} position="top" withArrow>
        <ActionIcon
          size={36}
          variant="default"
          aria-label={t('openTableConfiguration')}
          onClick={() => setConfigOpened(true)}
        >
          <TbSettings size={20} color="light-dark(var(--mantine-color-gray-7), var(--mantine-color-gray-3))" />
        </ActionIcon>
      </Tooltip>

      <Drawer opened={configOpened} onClose={() => setConfigOpened(false)} title={resolvedTitle} position="right">
        <Stack gap="md">
          <MultiSelect
            label={t('selectColumnsToDisplay')}
            placeholder={t('pickValue')}
            data={data}
            value={columns.filter(col => col.visible).map(col => String(col.key))}
            onChange={event => handleColumnToggle(event, true)}
            mb="md"
          />
        </Stack>
      </Drawer>
    </>
  )
}
